import { useEffect } from 'react'
import { useDataManagementApi } from '@/hooks'
import { useFeatureFlag } from '@/components/providers/FeatureFlagsProvider/useFeatureFlag'
import {
    TotalEmissionsReportSettings,
    GHGEmissionsReportSettings,
} from '@/hooks'

type ReportSettings = TotalEmissionsReportSettings | GHGEmissionsReportSettings

type UseScope2TypeAvailabilityOptions<T extends ReportSettings> = {
    reportSettings: T
    setReportSettings: (settings: Partial<T>) => void
}

type UseScope2TypeAvailabilityReturn = {
    isMarketBasedScope2Available: boolean | undefined
    isFetched: boolean
}

/**
 * Hook to manage market-based scope 2 availability based on energy contracts.
 * Automatically resets scope2type to 'locationBased' when market-based is not available,
 * unless scope2type is already 'locationBased' or 'all'.
 *
 * @param options - Report settings and setter function
 * @returns Object containing availability status and fetch status
 */
export const useScope2TypeAvailability = <T extends ReportSettings>(
    options: UseScope2TypeAvailabilityOptions<T>
): UseScope2TypeAvailabilityReturn => {
    const { reportSettings, setReportSettings } = options

    const isDataManagementEnabled = useFeatureFlag('dataManagement')

    const { useEnergyContractsQuery } = useDataManagementApi()
    const { data: energyContractsData, isFetched } = useEnergyContractsQuery(
        {},
        {
            enabled: isDataManagementEnabled,
        }
    )

    const isMarketBasedScope2Available = isDataManagementEnabled
        ? (energyContractsData?.items.length ?? 0) > 0
        : undefined

    useEffect(() => {
        if (!isDataManagementEnabled || !isFetched) {
            return
        }

        if (
            isMarketBasedScope2Available === false &&
            reportSettings.scope2type !== 'locationBased' &&
            reportSettings.scope2type !== 'all'
        ) {
            setReportSettings({
                ...reportSettings,
                scope2type: 'locationBased',
            } as Partial<T>)
        }
    }, [
        isDataManagementEnabled,
        isMarketBasedScope2Available,
        isFetched,
        reportSettings,
        setReportSettings,
    ])

    return {
        isMarketBasedScope2Available,
        isFetched,
    }
}
